import { ReactNode, useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { auth, db } from '../firebase'
import { onAuthStateChanged, User } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { callSessionLogin, startSessionRefresh, stopSessionRefresh } from '../services/sessionService'

function isRetryableError(e: unknown): boolean {
  const code = (e as { code?: string })?.code
  const message = String((e as Error)?.message ?? '')
  // أخطاء شبكة أو وقت انتهاء — إعادة المحاولة مرة واحدة
  return (
    code === 'unavailable' ||
    code === 'resource-exhausted' ||
    message.includes('network') ||
    message.includes('timeout') ||
    message.includes('fetch')
  )
}

export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [allowed, setAllowed] = useState(false)

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user: User | null) => {
      if (!user || !user.email) {
        console.info('[ProtectedRoute] redirect to login: no user or email')
        stopSessionRefresh()
        setAllowed(false)
        setLoading(false)
        return
      }

      const checkAdmin = async (retry = false): Promise<boolean> => {
        const ref = doc(db, 'admin_emails', user.email!)
        try {
          const snap = await getDoc(ref)
          return snap.exists()
        } catch (e) {
          if (!retry && isRetryableError(e)) {
            await new Promise((r) => setTimeout(r, 2000))
            return checkAdmin(true)
          }
          console.warn('[ProtectedRoute] getDoc(admin_emails) failed:', e)
          return false
        }
      }

      try {
        const isAllowed = await checkAdmin()
        setAllowed(isAllowed)
        if (isAllowed) {
          const token = await user.getIdToken()
          await callSessionLogin(token)
          startSessionRefresh(() => auth.currentUser?.getIdToken(true) ?? Promise.resolve(undefined))
        } else {
          console.info('[ProtectedRoute] redirect to login: user not in admin_emails')
          stopSessionRefresh()
        }
      } catch (e) {
        console.warn('[ProtectedRoute] error, redirecting to login:', e)
        setAllowed(false)
        stopSessionRefresh()
      } finally {
        setLoading(false)
      }
    })
    return () => {
      stopSessionRefresh()
      unsub()
    }
  }, [])

  if (loading) return null
  if (!allowed) return <Navigate to="/login" state={{ from: location }} replace />
  return <>{children}</>
}
